import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, Box, Typography, Table, TableBody, TableRow, TableCell, TableHead, Stack, Button, CircularProgress, Alert } from '@mui/material'
import { Order, TransactionType } from '../../proto/generated/order'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { getTradeHistory, TradeHistoryResponse } from '../../services/apis/orders'
import { toLocaleStringIn } from '../../util/general'
import { exchangeToJSON } from '../../proto/generated/common'
import useStyles from './utils/dialogStyles'

interface TradeHistoryDialogProps {
    open: boolean
    onClose: () => void
    onBack: () => void
    order: Order | null
    accountId: string
}

const TradeHistoryDialog: React.FC<TradeHistoryDialogProps> = ({ open, onClose, onBack, order, accountId }) => {
    const { classes } = useStyles()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [tradeData, setTradeData] = useState<TradeHistoryResponse | null>(null)

    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>> | null>(null)
    useEffect(() => {
        if (!order?.security) { setSecurity(null); return }
        const securityKey = getSecurityKey(order.security.exchange ?? 0, order.security.code ?? '')
        getSecurityAsync(securityKey).then(setSecurity)
    }, [order?.security])

    useEffect(() => {
        if (open && order?.orderId) {
            fetchTradeHistory()
        }
    }, [open, order?.orderId])

    const fetchTradeHistory = async () => {
        if (!order?.orderId || !accountId) return

        setLoading(true)
        setError(null)
        try {
            const data = await getTradeHistory(accountId, order.brokerOrderId)
            setTradeData(data)
        } catch (err: any) {
            setError(err.message || 'Failed to fetch trade history')
        } finally {
            setLoading(false)
        }
    }

    const formatTimestamp = (timestamp: string | number) => {
        if (!timestamp) return '—'

        const numTimestamp = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
        const adjustedTimestamp = numTimestamp < 10000000000 ? numTimestamp * 1000 : numTimestamp
        const date = new Date(adjustedTimestamp)

        if (isNaN(date.getTime())) return '—'

        return date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }



    if (!order) return null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                className: classes.dialog,
                sx: {
                    minHeight: 400,
                    maxHeight: 500,
                    maxWidth: 900
                }
            }}
        >
            <Box className={classes.header}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                            className={`${classes.transactionBadge} ${order.transactionType === TransactionType.BUY ? 'green-text' : 'red-text'}`}
                            sx={{
                                backgroundColor: order.transactionType === TransactionType.BUY ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                            }}
                        >
                            {order.transactionType === TransactionType.BUY ? 'BUY' : 'SELL'}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {security?.symbol || order.security?.code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {order.security?.exchange !== undefined ? exchangeToJSON(order.security.exchange) : ''}
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                        #{order?.brokerOrderId}
                    </Typography>
                </Stack>
            </Box>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flex: 1, alignItems: 'center' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, flex: 1 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : (
                    <Box sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider' } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        bgcolor: 'action.hover',
                                        '& .MuiTableCell-head': {
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            textTransform: 'uppercase',
                                            py: 1.5,
                                            px: 2,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider'
                                        }
                                    }}
                                >
                                    <TableCell>Trade ID</TableCell>
                                    <TableCell>Time</TableCell>
                                    <TableCell>Exch. time</TableCell>
                                    <TableCell>Exch. id</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell align="right">Qty.</TableCell>
                                    <TableCell align="right">Avg. price</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tradeData?.trades?.map((trade, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            },
                                            '& .MuiTableCell-root': {
                                                py: 1.5,
                                                px: 3,
                                                fontSize: 13,
                                                color: 'text.primary'
                                            }
                                        }}
                                    >
                                        <TableCell>{trade.tradeId}</TableCell>
                                        <TableCell>{formatTimestamp(trade.fillTimestamp)}</TableCell>
                                        <TableCell>{formatTimestamp(trade.fillTimestamp)}</TableCell>
                                        <TableCell>{order.brokerOrderId || order.orderId || '—'}</TableCell>
                                        <TableCell>CNC</TableCell>
                                        <TableCell align="right">{trade.quantity}</TableCell>
                                        <TableCell align="right">{toLocaleStringIn(trade.averagePrice / (security?.priceMultiplier ?? 100))}</TableCell>
                                    </TableRow>
                                ))}
                                {(!tradeData?.trades || tradeData.trades.length === 0) && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                                            No trades available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                <Box className={classes.footer} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: 'auto', py: 1.5 }}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onBack}
                            className={classes.button}
                            sx={{ minWidth: 80 }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClose}
                            className={classes.button}
                            sx={{ minWidth: 80 }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default TradeHistoryDialog
